import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { AnimatePresence } from 'framer-motion';

import { GlobalFonts } from '../../styles/GlobalFonts';
import { fivePracticaLocales } from '../../locales/firstScreen';
import { useSwipeable } from 'react-swipeable';
import CertificatePage from '../UI/CertificatePage';
import { CertificateModal } from '../UI/CertificateModal';
import {
  generateCertificateImage,
  uploadImageAndGetUrl,
  dataUrlToBlob,
} from '../../utils/certificate';

const Container = styled.div`
  background: #151515;
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  text-align: center;
  padding: 10px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: auto;
  overscroll-behavior: none;
  user-select: none;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const QuestionBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 27px 20px;
  width: 100%;
`;

const QuestionText = styled.span`
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: clamp(44px, 5vw, 26px);
  font-weight: 500;
  line-height: 90%;
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  text-align: center;
  white-space: pre-line;
  margin-top: 36px;
`;

const Description = styled.p`
  color: #b4b4b4;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  text-align: center;
  margin: 5px 19px;
  line-height: 145%;
  letter-spacing: -0.01em;
  white-space: pre-line;
`;

const HighlightBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 90%;
  max-width: 320px;
  padding: 10px 0;
  margin: 20px auto;
`;

const HighlightText = styled.span`
  color: #7354f2;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  white-space: pre-line;
  line-height: 1.4;
  text-shadow:
    0 0 5px rgba(115, 84, 242, 0.4),
    0 0 10px rgba(115, 84, 242, 0.3),
    0 0 15px rgba(115, 84, 242, 0.2),
    0 0 20px rgba(115, 84, 242, 0.15),
    0 0 25px rgba(115, 84, 242, 0.1);
`;

const ScrollContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 100px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  margin: 20px 10px 0;
`;

const StyledButton = styled.button`
  display: flex;
  align-items: center;
  align-self: stretch;
  text-align: left;
  padding: 18px 21px;
  margin-bottom: 10px;
  border-radius: 12px;
  border: 2px solid rgba(180, 180, 180, 0.2);
  background: none;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledPurpleButton = styled.button`
  display: flex;
  align-items: center;
  align-self: stretch;
  text-align: left;
  padding: 18px 21px;
  margin-bottom: 10px;
  border-radius: 12px;
  border: 2px solid #7354f2;
  background: #7354f2;
  transition: background 0.2s ease;

  &:hover {
    background: #5a3dd9;
    border-color: #5a3dd9;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const ButtonText = styled.span`
  flex: 1;
  color: #ffffff;
  font-size: 13px;
  font-weight: bold;
  font-family: 'Inter', sans-serif;
`;

const ButtonIcon = styled.img`
  width: 18px;
  height: 18px;
  object-fit: fill;
`;

const AnimatedDots = styled.span`
  &::after {
    content: '';
    animation: dots 2s infinite;
  }

  @keyframes dots {
    0%,
    20% {
      content: '';
    }
    40% {
      content: '.';
    }
    60% {
      content: '..';
    }
    80%,
    100% {
      content: '...';
    }
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled(motion.div)`
  background: #7354f2;
  width: 100%;
  max-width: 380px;
  min-height: 223px;
  border-radius: 20px;
  padding: 16px;
  margin: 20px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ModalTitle = styled.h3`
  font-family: 'Manrope', sans-serif;
  font-size: 28px;
  font-weight: 600;
  color: #ffffff;
  margin: 20px 0 16px 0;
  text-align: center;
  white-space: pre-line;
  line-height: 90%;
  letter-spacing: -0.02em;
  width: 100%;
`;

const ModalText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #ffffff;
  text-align: center;
  line-height: 145%;
  margin: 0 0 16px 0;
  white-space: pre-line;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex-grow: 1;
  padding: 0 10px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 22px;
  right: 22px;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  width: 12px;
  height: 12px;

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 15px;
    height: 2px;
    background: #ffffff;
    top: 50%;
    left: 50%;
  }

  &::before {
    transform: translate(-50%, -50%) rotate(45deg);
  }

  &::after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }
`;

const HomeButtonIcon = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
`;

const HomeButton = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  margin: 0;
  background-color: transparent;
`;

const modalVariants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.2,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
      delay: 0.1,
    },
  },
};

const modalContentVariants = {
  hidden: {
    scale: 0.8,
    opacity: 0,
  },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
  exit: {
    scale: 0.8,
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: 'easeIn',
    },
  },
};

interface FivePractica2Props {
  onRestart?: () => void;
  onSwipeRight?: () => void;
  toHome?: () => void;
}

const LANG_KEY = 'stonpools-lang';

const FivePractica2: React.FC<FivePractica2Props> = ({
  onRestart,
  onSwipeRight,
  toHome,
}) => {
  const [isBlockchainFeeModalOpen, setIsBlockchainFeeModalOpen] =
    useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [certificateImage, setCertificateImage] = useState<string>('');
  const [isCertificateModalOpen, setIsCertificateModalOpen] = useState(false);

  const handlers = useSwipeable({
    onSwipedRight: () => onSwipeRight?.(),
    preventScrollOnSwipe: false,
    trackMouse: true,
    delta: 50,
    swipeDuration: 500,
  });

  const lang = (localStorage.getItem(LANG_KEY) as 'ru' | 'en') || 'ru';
  const t = fivePracticaLocales[lang]?.[2] || {};

  const handleCertificateAction = async () => {
    // Если сертификат уже сгенерирован, показываем его
    if (certificateImage) {
      setIsCertificateModalOpen(true);
      return;
    }

    // Иначе генерируем сертификат
    try {
      setIsGenerating(true);

      // Создаём offscreen контейнер для рендера сертификата
      const offscreen = document.createElement('div');
      offscreen.style.position = 'fixed';
      offscreen.style.left = '-10000px';
      offscreen.style.top = '0';
      offscreen.style.zIndex = '-1';
      document.body.appendChild(offscreen);

      // Рендерим сертификат
      const root = document.createElement('div');
      offscreen.appendChild(root);

      // Временный рендер через React 18 API
      const { createRoot } = await import('react-dom/client');
      const rootInstance = createRoot(root);
      rootInstance.render(
        React.createElement(CertificatePage, {
          courseTitle: t.courseTitle,
          status: t.status,
        })
      );

      // Ждём кадр, чтобы DOM отрисовался
      await new Promise((r) => setTimeout(r, 50));

      const target = root.querySelector(
        '#certificate-print-root'
      ) as HTMLElement | null;
      if (!target) throw new Error('Certificate root not found');

      // Генерируем изображение вместо PDF
      const imageDataUrl = await generateCertificateImage(target);

      // Проверяем, что изображение сгенерировалось корректно
      if (
        !imageDataUrl ||
        imageDataUrl === 'data:,' ||
        imageDataUrl.length < 1000
      ) {
        throw new Error('Failed to generate certificate image');
      }

      console.log('Certificate image generated, length:', imageDataUrl.length);

      // Конвертируем в blob и загружаем в Supabase
      const blob = dataUrlToBlob(imageDataUrl);
      console.log('Blob created, size:', blob.size);

      // Загружаем в S3 и получаем URL (с метаданными языка)
      const uploadedUrl = await uploadImageAndGetUrl(blob, lang);
      console.log('Image uploaded to:', uploadedUrl);

      // Удаляем временный DOM
      rootInstance.unmount();
      document.body.removeChild(offscreen);

      // Сохраняем URL загруженного изображения
      setCertificateImage(uploadedUrl);

      // Показываем модальное окно с сертификатом
      setIsCertificateModalOpen(true);
    } catch (e) {
      console.error('Certificate generation error:', e);

      const errorMessage = `Ошибка генерации сертификата:\n\nКод: ${e instanceof Error ? e.name : 'Unknown'}\n\nСообщение: ${e instanceof Error ? e.message : String(e)}`;

      const telegramWebApp = (
        window as unknown as {
          Telegram?: { WebApp?: { showAlert: (message: string) => void } };
        }
      ).Telegram?.WebApp;

      if (telegramWebApp?.showAlert) {
        telegramWebApp.showAlert(errorMessage);
      } else {
        alert(errorMessage);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div {...handlers}>
      <GlobalFonts />
      <Container>
        <ScrollContainer>
          <HomeButton onClick={toHome}>
            <HomeButtonIcon src="/assets/homeIconPurple.svg" />
          </HomeButton>
          <QuestionBlock>
            <QuestionText>{t.congratsTitle}</QuestionText>
          </QuestionBlock>
          <Description>{t.congratsText}</Description>
          <ButtonContainer>
            <StyledButton
              onClick={handleCertificateAction}
              disabled={isGenerating}
            >
              <ButtonText>
                {isGenerating ? (
                  <>
                    {t.certificateIsGenerating}
                    <AnimatedDots />
                  </>
                ) : certificateImage ? (
                  t.viewCertificate
                ) : (
                  t.saveCertificate
                )}
              </ButtonText>
              <ButtonIcon src="/assets/Line.svg" />
            </StyledButton>

            <HighlightBox>
              <HighlightText>{t.advicesTitle}</HighlightText>
            </HighlightBox>

            <StyledPurpleButton
              onClick={() => window.open('https://app.ston.fi/swap', '_blank')}
            >
              <ButtonText>GO TO STON.fi</ButtonText>
              <ButtonIcon src="/assets/Line.svg" />
            </StyledPurpleButton>

            <StyledButton onClick={() => setIsBlockchainFeeModalOpen(true)}>
              <ButtonText>{t.telegramAdvice}</ButtonText>
              <ButtonIcon src="/assets/strelka.svg" />
            </StyledButton>

            <StyledButton
              onClick={() => {
                const telegramUrl =
                  lang === 'ru'
                    ? 'https://t.me/stonfichat_ru'
                    : 'https://t.me/stonfichat';
                window.open(telegramUrl, '_blank');
              }}
            >
              <ButtonText>{t.chatButton}</ButtonText>
              <ButtonIcon src="/assets/Line.svg" />
            </StyledButton>

            <br />
            <StyledButton onClick={onRestart}>
              <ButtonText>{t.restartButton}</ButtonText>
              <ButtonIcon src="/assets/Line.svg" />
            </StyledButton>
          </ButtonContainer>

          <AnimatePresence>
            {isBlockchainFeeModalOpen && (
              <ModalOverlay
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={modalVariants}
                onClick={() => setIsBlockchainFeeModalOpen(false)}
              >
                <ModalContent
                  onClick={(e) => e.stopPropagation()}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  variants={modalContentVariants}
                >
                  <CloseButton
                    onClick={() => setIsBlockchainFeeModalOpen(false)}
                  />
                  <ModalTitle>{t.telegramAdviceTitle}</ModalTitle>
                  <ModalText>{t.telegramAdviceText}</ModalText>
                  <StyledButton
                    onClick={() =>
                      window.open('https://t.me/stonfidex', '_blank')
                    }
                  >
                    <ButtonText>{t.subscribeButton}</ButtonText>
                    <ButtonIcon src="/assets/Line.svg" />
                  </StyledButton>
                </ModalContent>
              </ModalOverlay>
            )}
          </AnimatePresence>

          {/* Модальное окно с сертификатом */}
          <AnimatePresence>
            {isCertificateModalOpen && (
              <CertificateModal
                isOpen={isCertificateModalOpen}
                onClose={() => setIsCertificateModalOpen(false)}
                certificateImage={certificateImage}
                shareTitleText={t.shareButton}
                shareText={t.shareText}
                shareTextTwitter={t.shareTextTwitter}
                shareErrorText={t.shareErrorText}
                certificateAltText={t.certificateAltText}
              />
            )}
          </AnimatePresence>
        </ScrollContainer>
      </Container>
    </div>
  );
};

export default FivePractica2;
